import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getProjets, getArticles, articleImageUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiShield, FiUsers, FiDollarSign, FiStar, FiPhone, FiMail, FiArrowRight, FiArrowLeft, FiMapPin, FiHome, FiCheck, FiMenu, FiX, FiMap, FiTarget, FiTrendingUp, FiSend, FiLock, FiUser, FiLogIn } from 'react-icons/fi';
import logoChamfb from '../assets/logo-CHAMFB.png';

const LandingPage = () => {
  const [projets, setProjets] = useState<any[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const [mobileNav, setMobileNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [contactForm, setContactForm] = useState({ nom: '', email: '', sujet: '', message: '' });
  const [contactSent, setContactSent] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [loginId, setLoginId] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const { isAuthenticated, login, user } = useAuth();
  const navigate = useNavigate();
  const sliderRef = useRef<HTMLDivElement>(null);

  const scrollSlider = (direction: 'left' | 'right') => {
    if (!sliderRef.current) return;
    const cardWidth = 360;
    const gap = 24;
    const scrollAmount = cardWidth + gap;
    sliderRef.current.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
  };

  useEffect(() => {
    getProjets().then(r => setProjets(r.data)).catch(() => {});
    getArticles().then(r => setArticles(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleEspaceMembre = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isAuthenticated) {
      navigate('/admin');
    } else {
      setShowLogin(true);
      setMobileNav(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginId || !loginPassword) { setLoginError('Veuillez remplir tous les champs.'); return; }
    setLoginLoading(true);
    setLoginError('');
    try {
      await login(loginId, loginPassword);
      setShowLogin(false);
      setLoginId(''); setLoginPassword('');
      navigate('/admin');
    } catch (err: any) {
      setLoginError(err.response?.data?.error || 'Erreur de connexion');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div className="landing">
      {/* ========== NAVBAR ========== */}
      <header className={`landing-navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
        <div className="nav-inner">
          <a href="#accueil" className="landing-logo">
            <img src={logoChamfb} alt="CHA/MFB" className="logo-img" />
          </a>
          <nav className={`landing-nav ${mobileNav ? 'nav-open' : ''}`}>
            <a href="#accueil" onClick={() => setMobileNav(false)}>Accueil</a>
            <a href="#cooperative" onClick={() => setMobileNav(false)}>La Coopérative</a>
            <a href="#projets" onClick={() => setMobileNav(false)}>Projets</a>
            <a href="#adhesion" onClick={() => setMobileNav(false)}>Adhésion</a>
            <a href="#contact" onClick={() => setMobileNav(false)}>Contact</a>
            <a href="#" className="btn-espace" onClick={handleEspaceMembre}><FiUsers /> {isAuthenticated ? 'Mon Espace' : 'Espace Membre'}</a>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMobileNav(!mobileNav)}>
            {mobileNav ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </header>

      {/* ========== HERO ========== */}
      <section className="hero-section" id="accueil">
        <div className="hero-overlay" />
        <div className="hero-inner">
          <div className="hero-text">
            <div className="hero-badge">Ministère des Finances et du Budget</div>
            <h1>Construisons ensemble<br/>votre avenir immobilier</h1>
            <p>La Coopérative d'Habitat des Agents du MFB accompagne ses membres vers l'accession à la propriété avec des solutions d'habitat abordables et sécurisées.</p>
            <div className="hero-buttons">
              <a href="#projets" className="btn-hero-primary">Découvrir nos projets <FiArrowRight /></a>
              <a href="#" className="btn-hero-secondary" onClick={handleEspaceMembre}>{isAuthenticated ? 'Mon Espace' : 'Espace membre'}</a>
            </div>
            <div className="hero-stats-row">
              <div className="hero-stat-item">
                <span className="stat-num">3 253</span>
                <span className="stat-label">Membres</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat-item">
                <span className="stat-num">594</span>
                <span className="stat-label">Parcelles</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat-item">
                <span className="stat-num">15+</span>
                <span className="stat-label">Ans d'expérience</span>
              </div>
            </div>
          </div>
          <div className="hero-image-side">
            <div className="hero-img-main">
              <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80" alt="Maison moderne" />
            </div>
            <div className="hero-img-float">
              <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=300&q=80" alt="Clés maison" />
            </div>
          </div>
        </div>
      </section>

      {/* ========== ABOUT / COOPERATIVE ========== */}
      <section className="section-about" id="cooperative">
        <div className="container">
          <div className="about-grid">
            <div className="about-images">
              <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=500&q=80" alt="Immeuble" className="about-img-1" />
              <img src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=400&q=80" alt="Quartier résidentiel" className="about-img-2" />
              <div className="about-experience-badge">
                <span className="exp-num">15+</span>
                <span className="exp-txt">ans au service<br/>de nos membres</span>
              </div>
            </div>
            <div className="about-content">
              <div className="section-label">Qui sommes-nous</div>
              <h2>La Coopérative d'Habitat<br/>des Agents du MFB</h2>
              <p className="about-desc">
                Créée par et pour les agents du Ministère des Finances et du Budget du Sénégal, la CHA/MFB est une coopérative d'habitat qui vise à faciliter l'accession à la propriété de ses membres grâce à la mutualisation des ressources et un accompagnement personnalisé.
              </p>
              <div className="about-features">
                <div className="about-feat"><FiCheck /> Accès facilité à la propriété foncière</div>
                <div className="about-feat"><FiCheck /> Parcelles viabilisées dans des zones stratégiques</div>
                <div className="about-feat"><FiCheck /> Accompagnement juridique et technique</div>
                <div className="about-feat"><FiCheck /> Épargne progressive adaptée à votre budget</div>
                <div className="about-feat"><FiCheck /> Transparence totale dans la gestion</div>
              </div>
              <a href="#adhesion" className="btn-about">Comment adhérer <FiArrowRight /></a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== AVANTAGES ========== */}
      <section className="section-advantages">
        <div className="container">
          <div className="section-label center">Nos avantages</div>
          <h2 className="section-title-new">Pourquoi rejoindre la CHA/MFB ?</h2>
          <p className="section-sub">Une coopérative pensée pour répondre aux besoins réels de ses membres</p>
          <div className="adv-grid">
            <div className="adv-card">
              <div className="adv-icon-new"><FiDollarSign /></div>
              <h3>Prix avantageux</h3>
              <p>Des terrains à des prix nettement inférieurs au marché grâce à la force collective et aux négociations groupées.</p>
            </div>
            <div className="adv-card">
              <div className="adv-icon-new" style={{ background: '#fef3c7', color: '#d97706' }}><FiShield /></div>
              <h3>Sécurité juridique</h3>
              <p>Titres fonciers en bonne et due forme, accompagnement notarial et transparence totale des transactions.</p>
            </div>
            <div className="adv-card">
              <div className="adv-icon-new" style={{ background: '#fce7f3', color: '#db2777' }}><FiUsers /></div>
              <h3>Solidarité entre agents</h3>
              <p>Une communauté soudée de collègues du ministère, construisant ensemble un avenir meilleur.</p>
            </div>
            <div className="adv-card">
              <div className="adv-icon-new" style={{ background: '#d1fae5', color: '#059669' }}><FiTrendingUp /></div>
              <h3>Investissement rentable</h3>
              <p>Les terrains acquis prennent de la valeur, un placement sûr et rentable pour l'avenir de votre famille.</p>
            </div>
            <div className="adv-card">
              <div className="adv-icon-new" style={{ background: '#ede9fe', color: '#7c3aed' }}><FiMap /></div>
              <h3>Emplacements stratégiques</h3>
              <p>Des sites sélectionnés dans des zones à fort potentiel de développement : Bambilor, Lac Rose et plus.</p>
            </div>
            <div className="adv-card">
              <div className="adv-icon-new" style={{ background: '#ffedd5', color: '#ea580c' }}><FiTarget /></div>
              <h3>Suivi personnalisé</h3>
              <p>Un espace membre digital pour suivre vos versements, parcelles et documents en temps réel.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PROJETS ========== */}
      <section className="section-projects" id="projets">
        <div className="container">
          <div className="section-label center">Nos réalisations</div>
          <h2 className="section-title-new">Projets en cours</h2>
          <p className="section-sub">Découvrez nos programmes de parcelles viabilisées</p>
          <div className="projects-grid">
            {projets.length > 0 ? projets.map(p => (
              <div key={p.id} className="proj-card">
                <div className="proj-img">
                  <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&q=80" alt={p.libelle} />
                  <div className="proj-badge-overlay">En cours</div>
                </div>
                <div className="proj-body">
                  <h3>{p.libelle}</h3>
                  <div className="proj-location"><FiMapPin /> {p.localisation || 'Bambilor / Lac Rose, Sénégal'}</div>
                  <div className="proj-details">
                    <div className="proj-detail-item">
                      <span className="pd-num">{p.nb_parcelles || '594'}</span>
                      <span className="pd-label">Parcelles</span>
                    </div>
                    <div className="proj-detail-item">
                      <span className="pd-num">{p.nb_adherents || '3253'}</span>
                      <span className="pd-label">Adhérents</span>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="proj-card">
                <div className="proj-img">
                  <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&q=80" alt="Projet" />
                  <div className="proj-badge-overlay">En cours</div>
                </div>
                <div className="proj-body">
                  <h3>Projet Bambilor - Lac Rose</h3>
                  <div className="proj-location"><FiMapPin /> Bambilor / Lac Rose, Sénégal</div>
                  <div className="proj-details">
                    <div className="proj-detail-item"><span className="pd-num">594</span><span className="pd-label">Parcelles</span></div>
                    <div className="proj-detail-item"><span className="pd-num">3 253</span><span className="pd-label">Adhérents</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ========== ETAPES ADHESION ========== */}
      <section className="section-steps" id="adhesion">
        <div className="container">
          <div className="section-label center">Processus</div>
          <h2 className="section-title-new">Comment devenir membre ?</h2>
          <p className="section-sub">Un processus simple et transparent en 4 étapes</p>

          <div className="steps-timeline">
            {[
              { n: '01', icon: <FiUsers />, t: 'Adhésion', d: 'Remplissez le formulaire d\'adhésion et réglez votre part sociale de 5 000 FCFA pour officialiser votre entrée dans la coopérative.' },
              { n: '02', icon: <FiDollarSign />, t: 'Fond de roulement', d: 'Versez votre contribution au fond de roulement de 10 000 FCFA qui permet de financer les opérations courantes.' },
              { n: '03', icon: <FiTrendingUp />, t: 'Épargne progressive', d: 'Constituez votre épargne mensuelle de 50 000 FCFA selon le plan choisi, à votre rythme.' },
              { n: '04', icon: <FiHome />, t: 'Attribution terrain', d: 'Selon votre avancement d\'épargne, choisissez votre parcelle parmi les lots disponibles du projet.' },
            ].map((s, i) => (
              <div key={s.n} className="step-item">
                <div className="step-num-circle">{s.n}</div>
                <div className="step-content">
                  <div className="step-icon-small">{s.icon}</div>
                  <h3>{s.t}</h3>
                  <p>{s.d}</p>
                </div>
                {i < 3 && <div className="step-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA BANNER ========== */}
      <section className="cta-section">
        <div className="cta-bg-img" />
        <div className="cta-overlay" />
        <div className="cta-content">
          <h2>Prêt à construire votre avenir ?</h2>
          <p>Rejoignez plus de 3 000 agents qui ont déjà fait confiance à la CHA/MFB pour leur projet immobilier.</p>
          <div className="cta-buttons">
            <a href="#" className="btn-cta-primary" onClick={handleEspaceMembre}>Accéder à mon espace <FiArrowRight /></a>
            <a href="#contact" className="btn-cta-secondary">Nous contacter</a>
          </div>
        </div>
      </section>

      {/* ========== ARTICLES SLIDER ========== */}
      {articles.length > 0 && (
        <section className="section-news" id="actualites">
          <div className="container">
            <div className="news-header">
              <div>
                <div className="section-label">Blog</div>
                <h2 className="section-title-new">Actualités</h2>
              </div>
              {articles.length > 1 && (
                <div className="news-slider-nav">
                  <button className="slider-btn" onClick={() => scrollSlider('left')}><FiArrowLeft /></button>
                  <button className="slider-btn" onClick={() => scrollSlider('right')}><FiArrowRight /></button>
                </div>
              )}
            </div>
            <div className="news-slider" ref={sliderRef}>
              {articles.slice(0, 12).map((a) => {
                const defaultImg = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&q=80';
                const imgSrc = a.imageprincipale ? articleImageUrl(a.imageprincipale) : defaultImg;
                return (
                  <div key={a.id} className="news-slide-card" onClick={() => setSelectedArticle(a)}>
                    <div className="news-img">
                      <img src={imgSrc} alt={a.titre} />
                    </div>
                    <div className="news-body">
                      <div className="news-date-new">{(a.created || a.updated) ? new Date(a.created || a.updated).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</div>
                      <h3>{a.titre}</h3>
                      <p>{(a.article || '').replace(/<[^>]*>/g, '').substring(0, 100)}...</p>
                      <span className="news-read-more">Lire la suite <FiArrowRight /></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ========== CONTACT ========== */}
      <section className="section-contact" id="contact">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <div className="section-label">Contact</div>
              <h2>Vous avez des questions ?</h2>
              <p>Notre équipe est à votre disposition pour vous accompagner dans votre projet d'accession à la propriété.</p>
              <div className="contact-items">
                <div className="contact-item">
                  <div className="ci-icon"><FiMapPin /></div>
                  <div>
                    <strong>Adresse</strong>
                    <span>Rue Serré NDIAYE x Avenue Carde, Dakar, Sénégal</span>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="ci-icon"><FiPhone /></div>
                  <div>
                    <strong>Téléphone</strong>
                    <a href="tel:+221338210000">(+221) 33 821 00 00</a>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="ci-icon"><FiMail /></div>
                  <div>
                    <strong>Email</strong>
                    <a href="mailto:contact@cooperative-finances.com">contact@cooperative-finances.com</a>
                  </div>
                </div>
              </div>
            </div>
            <div className="contact-form-box">
              {contactSent ? (
                <div className="contact-success">
                  <FiCheck style={{ fontSize: '2.5rem', color: '#4CAF50' }} />
                  <h3>Message envoyé !</h3>
                  <p>Nous vous répondrons dans les plus brefs délais.</p>
                  <button className="btn-about" onClick={() => { setContactSent(false); setContactForm({ nom: '', email: '', sujet: '', message: '' }); }}>Envoyer un autre message</button>
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); setContactSent(true); }}>
                  <h3>Envoyez-nous un message</h3>
                  <div className="cf-row">
                    <div className="cf-group">
                      <label>Nom complet</label>
                      <input type="text" placeholder="Votre nom" value={contactForm.nom} onChange={e => setContactForm({...contactForm, nom: e.target.value})} required />
                    </div>
                    <div className="cf-group">
                      <label>Email</label>
                      <input type="email" placeholder="Votre email" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} required />
                    </div>
                  </div>
                  <div className="cf-group">
                    <label>Sujet</label>
                    <input type="text" placeholder="Objet de votre message" value={contactForm.sujet} onChange={e => setContactForm({...contactForm, sujet: e.target.value})} required />
                  </div>
                  <div className="cf-group">
                    <label>Message</label>
                    <textarea placeholder="Décrivez votre demande..." rows={5} value={contactForm.message} onChange={e => setContactForm({...contactForm, message: e.target.value})} required />
                  </div>
                  <button type="submit" className="btn-contact-submit"><FiSend /> Envoyer le message</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="footer-new">
        <div className="container">
          <div className="footer-top">
            <div className="footer-brand">
              <img src={logoChamfb} alt="CHA/MFB" className="footer-logo-img" />
              <p>Coopérative d'Habitat des Agents du Ministère des Finances et du Budget du Sénégal. Ensemble, construisons votre avenir.</p>
            </div>
            <div className="footer-links-col">
              <h4>Navigation</h4>
              <a href="#accueil">Accueil</a>
              <a href="#cooperative">La Coopérative</a>
              <a href="#projets">Nos projets</a>
              <a href="#adhesion">Adhésion</a>
            </div>
            <div className="footer-links-col">
              <h4>Espace membre</h4>
              <a href="#" onClick={handleEspaceMembre}>Connexion</a>
              <a href="#contact">Nous contacter</a>
            </div>
            <div className="footer-links-col">
              <h4>Contact</h4>
              <span>Dakar, Sénégal</span>
              <span>(+221) 33 821 00 00</span>
              <span>contact@cooperative-finances.com</span>
            </div>
          </div>
          <div className="footer-bottom-new">
            <span>&copy; {new Date().getFullYear()} CHA/MFB — Tous droits réservés.</span>
          </div>
        </div>
      </footer>

      {/* ========== ARTICLE POPUP ========== */}
      {selectedArticle && (
        <div className="article-modal-overlay" onClick={() => setSelectedArticle(null)}>
          <div className="article-modal" onClick={e => e.stopPropagation()}>
            <button className="article-modal-close" onClick={() => setSelectedArticle(null)}><FiX /></button>
            {selectedArticle.imageprincipale && (
              <div className="article-modal-image">
                <img src={articleImageUrl(selectedArticle.imageprincipale)} alt={selectedArticle.titre} />
              </div>
            )}
            <div className="article-modal-header">
              <div className="article-modal-date">
                {(selectedArticle.created || selectedArticle.updated) ? new Date(selectedArticle.created || selectedArticle.updated).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
              </div>
              <h2>{selectedArticle.titre}</h2>
              {selectedArticle.projet_libelle && (
                <span className="article-modal-badge">{selectedArticle.projet_libelle}</span>
              )}
            </div>
            <div className="article-modal-body" dangerouslySetInnerHTML={{ __html: selectedArticle.article || '' }} />
          </div>
        </div>
      )}

      {/* ========== LOGIN POPUP ========== */}
      {showLogin && (
        <div className="login-modal-overlay" onClick={() => setShowLogin(false)}>
          <div className="login-modal" onClick={e => e.stopPropagation()}>
            <button className="login-modal-close" onClick={() => setShowLogin(false)}><FiX /></button>
            <div className="login-modal-logo">
              <img src={logoChamfb} alt="CHA/MFB" style={{ height: 60 }} />
            </div>
            <h2>Connexion</h2>
            <p className="login-modal-sub">Accédez à votre espace membre</p>

            {loginError && <div className="alert alert-error">{loginError}</div>}

            <form onSubmit={handleLoginSubmit}>
              <div className="form-group">
                <label><FiUser /> Matricule / Email / Téléphone</label>
                <input type="text" className="form-input" placeholder="Entrez votre matricule" value={loginId} onChange={e => setLoginId(e.target.value)} required />
              </div>
              <div className="form-group">
                <label><FiLock /> Mot de passe</label>
                <input type="password" className="form-input" placeholder="Entrez votre mot de passe" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required />
              </div>
              <button type="submit" className="btn-login" disabled={loginLoading}>
                <FiLogIn /> {loginLoading ? 'Connexion...' : 'Se connecter'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
